<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import AppImage from '@uiKit/elements/Image.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import locationIcon from '@assets/images/propertyDetail/location-outlined.svg'
import mapIcon from '@assets/images/propertyDetail/map.svg'
import arrowOutwardIcon from '@assets/images/propertyDetail/arrow-outward.svg'
import PropertyDetailSection from './PropertyDetailSection.vue'

// LOCATION & LANDMARK (Figma 32590:61909): area line, the map with an
// "Open in Maps" pill. The landmark distance list needs a backend field that
// doesn't exist yet, so it isn't rendered.

interface Props {
  label: string
  embedUrl: string | null
  mapsUrl: string | null
}

defineProps<Props>()

// The pill's grey glyphs are drawn as masks so they take the theme's muted
// text colour (the raw SVGs are fixed #414651, unreadable on a dark pill).
const maskStyle = (icon: string) => ({ maskImage: `url("${icon}")` })
</script>

<template>
  <PropertyDetailSection v-if="label || embedUrl" title="Location & Landmark" gap="20px">
    <Box direction="col" gap="14px">
      <Box v-if="label" align="center" gap="6px">
        <AppImage :src="locationIcon" alt="" aria-hidden="true" class="pd-location__pin" />
        <Text
          type="body"
          weight="medium"
          color="var(--p-sliceal-gray-900)"
          is="span"
          style="line-height: 20px"
          >{{ label }}</Text
        >
      </Box>

      <Box class="pd-location__map">
        <!-- Raw <iframe> — third-party map embed, no kit citizen (§19 exception). -->
        <iframe
          v-if="embedUrl"
          :src="embedUrl"
          class="pd-location__frame"
          loading="lazy"
          referrerpolicy="no-referrer-when-downgrade"
          title="Property location map"
        />
        <Box
          v-else
          direction="col"
          align="center"
          justify="center"
          gap="2"
          class="pd-location__fallback"
        >
          <PiIcon name="map" class="pd-location__fallback-icon" />
          <Text type="body" color="muted">Map not available</Text>
        </Box>

        <Box
          v-if="mapsUrl"
          is="a"
          :href="mapsUrl"
          target="_blank"
          rel="noopener noreferrer"
          align="center"
          gap="1"
          class="pd-location__open"
          aria-label="Open in Maps (opens in a new tab)"
        >
          <Box
            class="pd-location__glyph pd-location__glyph--map"
            :style="maskStyle(mapIcon)"
            aria-hidden="true"
          />
          <Text
            type="caption"
            weight="medium"
            color="muted"
            align="center"
            is="span"
            style="line-height: 18px"
            >Open in Maps</Text
          >
          <Box
            class="pd-location__glyph pd-location__glyph--arrow"
            :style="maskStyle(arrowOutwardIcon)"
            aria-hidden="true"
          />
        </Box>
      </Box>
    </Box>
  </PropertyDetailSection>
</template>

<style scoped>
.pd-location__pin {
  display: block;
  flex-shrink: 0;
  width: 15px;
  height: 15px;
}

.pd-location__map {
  position: relative;
  height: 222px;
  border-radius: 10px;
  overflow: hidden;
  background: var(--p-surface-100);
}

.pd-location__frame {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
  /* The design renders the map in greys so the white pill stands out. */
  filter: grayscale(1) contrast(0.95);
}

.pd-location__fallback {
  height: 100%;
}

.pd-location__fallback-icon {
  font-size: 28px;
  color: var(--p-surface-400);
}

/* 30px pill whose top sits 168px into the 222px map → 24px off the bottom. */
.pd-location__open {
  position: absolute;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  padding: 6px 12px;
  border-radius: 9px;
  background: var(--p-surface-0);
  text-decoration: none;
  white-space: nowrap;
  /* Shadow/md */
  box-shadow:
    0 2px 4px -2px rgba(10, 13, 18, 0.06),
    0 4px 8px -2px rgba(10, 13, 18, 0.1);
}

.pd-location__open:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.pd-location__glyph {
  flex-shrink: 0;
  background-color: var(--p-text-muted-color);
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
}

.pd-location__glyph--map {
  width: 20px;
  height: 20px;
}

.pd-location__glyph--arrow {
  width: 14px;
  height: 14px;
}
</style>
